import React, { Fragment } from 'react';
import styled from 'styled-components';
import Tooltip from 'rc-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'i18n';
import flexbox from 'styles/utils/flexbox';

function FlightDetailsComponent({
  departureTime,
  departureAirport,
  departureAirportName,
  arrivalTime,
  destinationAirport,
  destinationAirportName,
  flightTime,
  carrierCode,
  carrierName,
  carrierLogo,
  flightNumber,
  operatingCarrierCode,
  operatingCarrierName,
  operatingCarrierLogo,
  operatingFlightNumber,
  legIsNonstop,
  firstFlight,
  lastFlight,
  arrivalAndDepartureAirportAreNotTheSame,
  initialAndLastAirportAreNotTheSame,
  airlineWhitelabel,
  hasLayoverAfter,
  hasLayoverBefore,
}) {
  return (
    <Root firstFlight={!legIsNonstop && firstFlight} lastFlight={!legIsNonstop && lastFlight}>
      <Times>
        <DestinationTime airlineWhitelabel={airlineWhitelabel}>{departureTime}</DestinationTime>
        <FlightTime>{flightTime}</FlightTime>
        <DestinationTime airlineWhitelabel={airlineWhitelabel}>{arrivalTime}</DestinationTime>
      </Times>
      <Decoration>
        <Point small={hasLayoverBefore} />
        <Line />
        <Point small={hasLayoverAfter} />
      </Decoration>
      <FlightInfo>
        <DestinationName airlineWhitelabel={airlineWhitelabel}>
          <Icon>
            <FontAwesomeIcon icon='plane-departure' />
          </Icon>
          <div>
            {departureAirportName}
            <DestinationCode>({departureAirport})</DestinationCode>
            {firstFlight && arrivalAndDepartureAirportAreNotTheSame && (
              <Tooltip
                placement='top'
                trigger={['hover']}
                overlayStyle={{ opacity: 1 }}
                overlay={
                  <FormattedMessage
                    id='arrivalAndDepartureAirportAreNotTheSame'
                    description='Arrival and Departure Airport are Not the same.'
                    defaultMessage='Arrival and Departure Airport are Not the same.'
                  />
                }
                destroyTooltipOnHide
              >
                <Warning icon='info-circle' />
              </Tooltip>
            )}
          </div>
        </DestinationName>
        <CarrierServicesContent>
          <Carrier>
            <CarrierLogo src={carrierLogo} />
            <CarrierName airlineWhitelabel={airlineWhitelabel}>{carrierName}</CarrierName>
            <span>&minus;</span>
            <FlightNumber>{`${carrierCode}${flightNumber}`}</FlightNumber>
          </Carrier>
          {operatingFlightNumber && (
            <Fragment>
              <OperatedByMessage>
                <FormattedMessage
                  id={'operatedBy.label'}
                  description={'Operated by:'}
                  defaultMessage={'Operated by:'}
                />
              </OperatedByMessage>
              <Carrier>
                <CarrierLogo src={operatingCarrierLogo} />
                <CarrierName airlineWhitelabel={airlineWhitelabel}>{operatingCarrierName}</CarrierName>
                <span>&minus;</span>
                <FlightNumber>{`${operatingCarrierCode}${operatingFlightNumber}`}</FlightNumber>
              </Carrier>
            </Fragment>
          )}
        </CarrierServicesContent>
        <DestinationName airlineWhitelabel={airlineWhitelabel}>
          <Icon>
            <FontAwesomeIcon icon='plane-arrival' />
          </Icon>
          <div>
            {destinationAirportName}
            <DestinationCode>({destinationAirport})</DestinationCode>
            {lastFlight && initialAndLastAirportAreNotTheSame && (
              <Tooltip
                placement='top'
                trigger={['hover']}
                overlayStyle={{ opacity: 1 }}
                overlay={
                  <FormattedMessage
                    id='initialAndLastAirportAreNotTheSame'
                    description='Departure and Return airport are not the same.'
                    defaultMessage='Departure and Return airport are not the same.'
                  />
                }
                destroyTooltipOnHide
              >
                <Warning icon='info-circle' />
              </Tooltip>
            )}
          </div>
        </DestinationName>
      </FlightInfo>
    </Root>
  );
}

export default FlightDetailsComponent;

const Root = styled.div`
  ${flexbox({
    justifyContent: 'space-between',
  })}

  width: 336px;
  padding: 20px;
  padding-bottom: ${({ firstFlight }) => firstFlight && '5px'};
  padding-top: ${({ lastFlight }) => lastFlight && '5px'};
  box-sizing: content-box;
`;

const Times = styled.div`
  ${flexbox({
    flexDirection: 'column',
    justifyContent: 'space-between',
  })}
  width: 50%;
`;

const FlightTime = styled.div`
  font-size: 1.4em;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DestinationTime = styled.div`
  font-size: 1.6em;
  font-weight: ${({ airlineWhitelabel }) => (airlineWhitelabel && airlineWhitelabel.isAirBaltic ? 500 : 700)};
  color: ${({ theme }) => theme.colors.text.default};
`;

const Decoration = styled.div`
  ${flexbox({
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
  })}
  width: 50%;
`;

const Point = styled.div`
  width: 5px;
  height: 5px;
  background: ${({ small, theme }) => (small ? theme.colors.secondary.default : 'transparent')};
  border: 2px solid ${({ theme }) => theme.colors.background.grey};
  border-radius: 100%;
`;

const Line = styled.div`
  height: calc(100% - 30px);
  width: 1px;
  background: ${({ theme }) => theme.colors.background.grey};
`;

const FlightInfo = styled.div`
  flex: 1 0 75%;
`;

const DestinationName = styled.div`
  ${flexbox()}
  font-size: 1.6em;
  font-weight: ${({ airlineWhitelabel }) => (airlineWhitelabel && airlineWhitelabel.isAirBaltic ? 500 : 700)};
  color: ${({ theme }) => theme.colors.text.default};
`;

const DestinationCode = styled.span`
  margin-left: 8px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.light};
`;

const Warning = styled(FontAwesomeIcon)`
  margin-left: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.severity.error[0]};
`;

const Icon = styled.div`
  display: inline;
  margin-right: 7px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.light};
`;

const CarrierServicesContent = styled.div`
  margin: 20px 0;
  padding: 12px;
  background: ${({ theme }) => theme.colors.background.light};
  border: 1px solid ${({ theme }) => theme.colors.background.grey};
  border-radius: 3px;
  box-shadow: 0px 3px 18px 0 rgba(0, 0, 0, 0.06);
`;

const Carrier = styled.div`
  ${flexbox({
    alignItems: 'center',
  })}
  gap: 8px;
`;

const CarrierLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: scale-down;
`;

const CarrierName = styled.div`
  font-size: 1.4em;
  font-weight: ${({ airlineWhitelabel }) => (airlineWhitelabel && airlineWhitelabel.isAirBaltic ? 500 : 700)};
  color: ${({ theme }) => theme.colors.text.default};
`;

const FlightNumber = styled.div`
  font-size: 1.4em;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.default};
`;

const OperatedByMessage = styled.div`
  margin-top: 8px;
  margin-bottom: 6px;
  font-size: 1.4em;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.adjacent.default};
`;
